import type { OfferRenderFailureCode, OfferStatus } from '@today/types'

/**
 * View models of the offer screens (TO-466 offer-generation-status). The views
 * are pure: the page hooks map API DTOs into these shapes and pass a `t`
 * translate function, so no view touches i18n, data fetching or business rules.
 */

/** Translate a catalog key (react-i18next's `t`, or a stub in stories/tests). */
export type Translate = (key: string, vars?: Record<string, string | number>) => string

/** What an artifact tile shows; the catalog key `artifact.<value>` labels it. */
export enum OfferArtifactViewState {
	PENDING = 'PENDING',
	READY = 'READY',
	FAILED = 'FAILED',
	TIMED_OUT = 'TIMED_OUT',
}

/** The send action the detail offers (never gated on readiness); `NONE` renders no button. */
export enum OfferSendAction {
	NONE = 'NONE',
	SEND = 'SEND',
	RESEND = 'RESEND',
}

/** Which version line shows; the catalog key `detail.version.<value>` words it. */
export enum OfferVersionKind {
	/** The first send is rendering `pending`. */
	SENDING = 'SENDING',
	/** `current` is live while the re-version `pending` renders. */
	RENDERING = 'RENDERING',
	/** `current` is live, nothing pending. */
	LIVE = 'LIVE',
}

/** Which body the offer detail renders. */
export enum OfferDetailState {
	LOADING = 'LOADING',
	LOADED = 'LOADED',
	NOT_FOUND = 'NOT_FOUND',
	ERROR = 'ERROR',
}

/** The version line: its kind plus the live (`current`) and/or rendering (`pending`) version. */
export interface OfferVersionView {
	kind: OfferVersionKind
	current?: number
	pending?: number
}

/** The in-flight render's two artifact tiles. */
export interface OfferTilesView {
	landing: OfferArtifactViewState
	pdf: OfferArtifactViewState
}

/** Why the offer's last render did not publish, and which version it was. */
export interface OfferRenderFailureView {
	code: OfferRenderFailureCode
	renderVersion: number
}

export interface OfferRowView {
	id: string
	status: OfferStatus
	/** Already formatted for display. */
	createdAt: string
}
